import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Banknote,
  ShoppingCart,
  Clock,
  Lock,
  Plus,
  Minus,
  ArrowLeft,
  RefreshCw,
  Search,
  Sparkles,
  Info,
  Receipt,
  History,
  Calendar,
  UtensilsCrossed,
  Trash2,
  PauseCircle,
  PlayCircle,
  X,
  Eye,
  Printer,
  XCircle,
  Copy,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  LayoutGrid,
  List,
} from 'lucide-react';
import apiService from '../services/api';
import { buildCategoryFilterChips, buildProduitCountMaps, normalizeRubriqueNom, rubriqueCountKey, sortProduitsForPos } from '../utils/rubriqueCounts';
import { matchSearchText } from '../utils/normalizeSearchText';
import { buildRubriqueCoverImages, getMenuTilePalette } from '../utils/menuNavVisuals';
import { formatMoney } from '../utils/money';
import { DEVISE_DEFAUT } from '../config/devises';
import { getProductImageUrl } from '../utils/productImage';
import CaissePaymentModal from '../components/caisse/CaissePaymentModal';
import CaisseProductModal from '../components/caisse/CaisseProductModal';
import CaisseOptionsModal from '../components/caisse/CaisseOptionsModal';
import CaissePoidsModal from '../components/caisse/CaissePoidsModal';
import CaissePanierOptions from '../components/caisse/CaissePanierOptions';
import CaisseClientPicker from '../components/caisse/CaisseClientPicker';
import CaisseRecompensePanel from '../components/caisse/CaisseRecompensePanel';
import CaisseRecompenseConfirmDialog from '../components/caisse/CaisseRecompenseConfirmDialog';
import { useCaisseFidelite } from '../hooks/useCaisseFidelite';
import {
  buildTicketFideliteFields,
  buildImmediateVenteFideliteFields,
  resolveCaisseImmediatePayGate,
  resolveCaisseConfirmedRewardPayGate,
  estimateCaissePayableTotal,
  isRewardCatalogueVenteError,
} from '../utils/caisseFidelite';
import { createCaisseIdempotencyKey } from '../utils/caisseIdempotency';
import {
  buildCaisseFideliteDisplay,
  formatFideliteDuplicataBlock,
} from '../utils/caisseTicketFideliteDisplay';
import {
  hasMenuOptions,
  productNeedsCustomization,
  mergeProduitAvecOptions,
  buildLineKey,
  formatOptionsTicketBlock,
  isVenteAuPoids,
  formatPrixProduitCaisse,
} from '../utils/caisseProduct';

function CaisseToast({ toasts, onDismiss }) {
  return (
    <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border backdrop-blur-sm animate-[slideIn_0.3s_ease-out] max-w-sm ${
            t.type === 'success'
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : t.type === 'error'
                ? 'bg-red-600 border-red-500 text-white'
                : 'bg-slate-800 border-slate-700 text-white'
          }`}
        >
          {t.type === 'success' && <CheckCircle className="h-5 w-5 shrink-0" />}
          {t.type === 'error' && <XCircle className="h-5 w-5 shrink-0" />}
          {t.type === 'info' && <Info className="h-5 w-5 shrink-0" />}
          <span className="text-sm font-medium flex-1">{t.message}</span>
          <button type="button" onClick={() => onDismiss(t.id)} className="p-0.5 rounded hover:bg-white/20 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function CaisseConfirmModal({ open, title, children, confirmLabel, cancelLabel, onConfirm, onCancel, variant, processing }) {
  if (!open) return null;
  const btnClass = variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-emerald-600 hover:bg-emerald-700 text-white';
  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            {variant === 'danger' && <AlertTriangle className="h-5 w-5 text-red-500" />}
            {title}
          </h3>
        </div>
        <div className="px-6 py-5">{children}</div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
          <button type="button" onClick={onCancel} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200">
            {cancelLabel || 'Annuler'}
          </button>
          <button type="button" onClick={onConfirm} disabled={processing} className={`px-5 py-2.5 rounded-xl font-semibold disabled:opacity-50 ${btnClass}`}>
            {confirmLabel || 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}

const MODES = {
  accueil: 'accueil',
  vendre: 'vendre',
  encaisser: 'encaisser',
  cloture: 'cloture',
  historique: 'historique',
};

const TYPE_ICONS = {
  COMMANDE: ShoppingCart,
  RENDEZ_VOUS: Calendar,
  RESERVATION_TABLE: UtensilsCrossed,
};

const CAISSE_COMMERCANT_KEY = 'clickorico_caisse_commercant_id';
const CAISSE_PANIER_KEY = 'clickorico_caisse_panier';
const CAISSE_TICKET_KEY = 'clickorico_caisse_ticket_id';
const CAISSE_CONTACT_KEY = 'clickorico_caisse_contact';
const CAISSE_AFFICHAGE_KEY = 'clickorico_caisse_affichage';

const AFFICHAGE_CATALOGUE = {
  compact: 'compact',
  menu: 'menu',
};

const readAffichageCatalogue = () => {
  try {
    const saved = localStorage.getItem(CAISSE_AFFICHAGE_KEY);
    if (saved === AFFICHAGE_CATALOGUE.menu || saved === AFFICHAGE_CATALOGUE.compact) return saved;
  } catch { /* */ }
  return AFFICHAGE_CATALOGUE.compact;
};

const resolveCommercantId = (searchParams) => {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) {
    try {
      sessionStorage.setItem(CAISSE_COMMERCANT_KEY, fromUrl);
    } catch {
      /* ignore */
    }
    return fromUrl;
  }
  try {
    const stored = sessionStorage.getItem(CAISSE_COMMERCANT_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    const id = user.commercantId || user.commercant?.id || null;
    if (id) {
      try {
        sessionStorage.setItem(CAISSE_COMMERCANT_KEY, id);
      } catch {
        /* ignore */
      }
    }
    return id;
  } catch {
    return null;
  }
};

function serializeLigneProduitPourVente(l) {
  const opts = l.options && Object.keys(l.options).length > 0 ? l.options : null;
  const line = {
    produitId: l.produitId,
    quantite: l.quantite,
    prixUnitaire: Number(l.prixUnitaire),
  };
  if (l.poidsSouhaite != null && l.poidsSouhaite !== '') {
    line.poidsSouhaite = Number(l.poidsSouhaite);
  }
  if (l.typeDecoupe) {
    line.typeDecoupe = l.typeDecoupe;
  }
  if (opts) {
    line.options = opts;
    line.commentaire = formatOptionsTicketBlock(opts);
  }
  return line;
}

function ProductTile({ produit, fmt, qty, onTap, onInfo, loading }) {
  const imageUrl = getProductImageUrl(produit);
  const auPoids = isVenteAuPoids(produit);
  const prixLabel = formatPrixProduitCaisse(produit, fmt);
  const hasPromo =
    !auPoids && produit.prixPromo != null && produit.prixPromo < produit.prix;
  const disabled = produit.indisponible || produit.desactiveParIngredient;
  const avecOptions = hasMenuOptions(produit) || produit.hasOptions === true;

  return (
    <div
      className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
        disabled
          ? 'opacity-50 border-slate-100'
          : loading
            ? 'opacity-70 border-emerald-300'
            : 'border-slate-100 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 active:scale-[0.98]'
      }`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onTap(produit)}
        className="w-full text-left disabled:cursor-not-allowed"
      >
        <div className="aspect-square bg-slate-100 relative overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={produit.nom}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <Sparkles className="h-10 w-10 text-slate-300" />
            </div>
          )}
          {qty > 0 && (
            <span className="absolute top-2 right-2 min-w-[1.75rem] h-7 px-1.5 flex items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold shadow">
              {qty}
            </span>
          )}
          {hasPromo && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-rose-500 text-white text-[10px] font-bold uppercase">
              Promo
            </span>
          )}
          {auPoids && !disabled && (
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-amber-600 text-white text-[10px] font-bold">
              Poids / prix
            </span>
          )}
          {avecOptions && !disabled && !auPoids && (
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-violet-600 text-white text-[10px] font-bold">
              Options
            </span>
          )}
        </div>
        <div className="p-3">
          {produit.categorie && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 truncate">
              {produit.sousCategorie
                ? `${produit.categorie} · ${produit.sousCategorie}`
                : produit.categorie}
            </p>
          )}
          <p className="font-semibold text-slate-900 text-sm leading-tight line-clamp-2 min-h-[2.5rem]">
            {produit.nom}
          </p>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-emerald-600 font-bold">{prixLabel}</span>
            {hasPromo && (
              <span className="text-xs text-slate-400 line-through">{fmt(produit.prix)}</span>
            )}
          </div>
        </div>
      </button>
      {!disabled && onInfo && qty === 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onInfo(produit);
          }}
          className="absolute top-2 left-2 p-1.5 rounded-full bg-white/90 shadow text-slate-500 hover:text-slate-800 z-10"
          title="Détail"
        >
          <Info className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function MenuNavTile({ label, count, onClick, imageUrl, previewImages, paletteIndex = 0 }) {
  const palette = getMenuTilePalette(paletteIndex);
  const thumbUrl = imageUrl || previewImages?.[0] || null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-stretch gap-3 w-full text-left rounded-2xl border-2 bg-white px-3 py-[1.125rem] min-h-[8.25rem] transition active:scale-[0.98] hover:bg-slate-50/80 ${palette.border}`}
    >
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 self-center min-h-[5.25rem] pr-1">
        <p className="font-semibold text-[0.9375rem] leading-5 text-slate-900 line-clamp-2 min-h-[2.5rem]">
          {label}
        </p>
        {count != null && (
          <p className={`text-xs font-semibold leading-4 tabular-nums ${palette.accent}`}>
            {count} produit{count !== 1 ? 's' : ''}
          </p>
        )}
      </div>
      <div className={`relative shrink-0 self-center w-[5.25rem] h-[5.25rem] rounded-xl overflow-hidden ring-1 ring-inset ${palette.border}`}>
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${palette.gradient} flex items-center justify-center`}>
            <span className="text-xl font-bold text-white">{label.charAt(0).toUpperCase()}</span>
          </div>
        )}
      </div>
    </button>
  );
}

export default function Caisse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const commercantId = useMemo(() => resolveCommercantId(searchParams), [searchParams]);
  const [view, setView] = useState(() => {
    try {
      const savedPanier = sessionStorage.getItem(CAISSE_PANIER_KEY);
      if (savedPanier) {
        const parsed = JSON.parse(savedPanier);
        if (Array.isArray(parsed) && parsed.length > 0) return MODES.vendre;
      }
    } catch { /* */ }
    return MODES.accueil;
  });
  const [session, setSession] = useState(null);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionRecap, setSessionRecap] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [codePromo, setCodePromo] = useState('');
  const [promoReduction, setPromoReduction] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoValide, setPromoValide] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const resetPaiement = () => {
    setPaymentError('');
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catalogue, setCatalogue] = useState({ produits: [], prestations: [] });
  const [catalogueRubriques, setCatalogueRubriques] = useState([]);
  const [aEncaisser, setAEncaisser] = useState([]);
  const [panier, setPanier] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CAISSE_PANIER_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [fondCaisse, setFondCaisse] = useState('');
  const [especesComptees, setEspecesComptees] = useState('');
  const [contactNom, setContactNom] = useState(() => {
    try { return sessionStorage.getItem(CAISSE_CONTACT_KEY) || ''; } catch { return ''; }
  });
  /** F2.4b1 — client fidélité identifié (≠ contact libre guest) */
  const [selectedClient, setSelectedClient] = useState(null);
  const [encaissementTarget, setEncaissementTarget] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [devise, setDevise] = useState(DEVISE_DEFAUT);
  const [searchQuery, setSearchQuery] = useState('');
  const [categorieFilter, setCategorieFilter] = useState('TOUS');
  const [sousCategorieFilter, setSousCategorieFilter] = useState('TOUS');
  const [affichageCatalogue, setAffichageCatalogue] = useState(readAffichageCatalogue);
  const [menuShowAll, setMenuShowAll] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [optionsProduct, setOptionsProduct] = useState(null);
  const [poidsProduct, setPoidsProduct] = useState(null);
  const [catalogueTab, setCatalogueTab] = useState('produits');
  const [modulePrestations, setModulePrestations] = useState(false);
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [ticketsOuverts, setTicketsOuverts] = useState([]);
  const [activeTicketId, setActiveTicketId] = useState(() => {
    try { return sessionStorage.getItem(CAISSE_TICKET_KEY) || null; } catch { return null; }
  });
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketFideliteDisplay, setTicketFideliteDisplay] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [fermetureWarning, setFermetureWarning] = useState(null);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  /** Conservée pendant retries ; renouvelée seulement après succès */
  const paymentIdempotencyKeyRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [annulMotif, setAnnulMotif] = useState('');
  const [showClotureConfirm, setShowClotureConfirm] = useState(false);
  const [showSavModal, setShowSavModal] = useState(false);
  const [savTab, setSavTab] = useState('articles');
  const [savLineQty, setSavLineQty] = useState({});
  const [savMontantLibre, setSavMontantLibre] = useState('');
  const [savMotif, setSavMotif] = useState('');
  const [savMethode, setSavMethode] = useState('ESPECES');
  const [savLoading, setSavLoading] = useState(false);

  useEffect(() => {
    try { sessionStorage.setItem(CAISSE_PANIER_KEY, JSON.stringify(panier)); } catch { /* */ }
  }, [panier]);

  useEffect(() => {
    try {
      if (activeTicketId) sessionStorage.setItem(CAISSE_TICKET_KEY, activeTicketId);
      else sessionStorage.removeItem(CAISSE_TICKET_KEY);
    } catch { /* */ }
  }, [activeTicketId]);

  useEffect(() => {
    try {
      if (contactNom) sessionStorage.setItem(CAISSE_CONTACT_KEY, contactNom);
      else sessionStorage.removeItem(CAISSE_CONTACT_KEY);
    } catch { /* */ }
  }, [contactNom]);

  const showToast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    if (selectedProduct && isVenteAuPoids(selectedProduct)) {
      setPoidsProduct(selectedProduct);
      setSelectedProduct(null);
    }
  }, [selectedProduct]);

  const fmt = useCallback((amount) => formatMoney(amount, devise), [devise]);

  const totalPanier = useMemo(
    () => panier.reduce((s, l) => s + l.prixUnitaire * l.quantite, 0),
    [panier]
  );

  const fidelite = useCaisseFidelite({
    active: view === MODES.vendre && !!session,
    commercantId,
    clientUserId: selectedClient?.id || null,
    sousTotal: totalPanier,
    reductionPromo: Number(promoReduction) || 0,
    ticketId: activeTicketId,
  });

  const panierQtyMap = useMemo(() => {
    const m = {};
    panier.forEach((l) => {
      m[l.produitId] = (m[l.produitId] || 0) + l.quantite;
    });
    return m;
  }, [panier]);

  const categories = useMemo(() => {
    const ordered = buildCategoryFilterChips(catalogue.produits, catalogueRubriques);
    return ['TOUS', ...ordered];
  }, [catalogue.produits, catalogueRubriques]);

  const produitCountMaps = useMemo(
    () => buildProduitCountMaps(catalogue.produits),
    [catalogue.produits]
  );

  const rubriqueCoverImages = useMemo(
    () => buildRubriqueCoverImages(catalogue.produits),
    [catalogue.produits]
  );

  const categoryMenuItems = useMemo(
    () => categories.filter((cat) => cat !== 'TOUS'),
    [categories]
  );

  const sousCategories = useMemo(() => {
    if (categorieFilter === 'TOUS') return [];
    const cat = catalogueRubriques.find((c) => c.nom === categorieFilter);
    const fromRegistry = (cat?.sousCategories || [])
      .filter((s) => s.actif !== false)
      .map((s) => s.nom);
    if (fromRegistry.length) return fromRegistry;

    const seen = new Map();
    catalogue.produits.forEach((p) => {
      if (rubriqueCountKey(p.categorie) !== rubriqueCountKey(categorieFilter)) return;
      const sub = normalizeRubriqueNom(p.sousCategorie);
      if (sub && !seen.has(rubriqueCountKey(sub))) seen.set(rubriqueCountKey(sub), sub);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [categorieFilter, catalogueRubriques, catalogue.produits]);

  useEffect(() => {
    setSousCategorieFilter('TOUS');
  }, [categorieFilter]);

  const produitsFiltres = useMemo(() => {
    const q = searchQuery.trim();
    const filtered = catalogue.produits.filter((p) => {
      if (
        categorieFilter !== 'TOUS' &&
        rubriqueCountKey(p.categorie) !== rubriqueCountKey(categorieFilter)
      ) {
        return false;
      }
      if (
        sousCategorieFilter !== 'TOUS' &&
        sousCategorieFilter !== '__ALL__' &&
        rubriqueCountKey(p.sousCategorie) !== rubriqueCountKey(sousCategorieFilter)
      ) {
        return false;
      }
      if (!q) return true;
      return (
        matchSearchText(p.nom, q) ||
        matchSearchText(p.categorie, q) ||
        matchSearchText(p.sousCategorie, q) ||
        matchSearchText(p.description, q)
      );
    });
    return sortProduitsForPos(filtered, catalogueRubriques);
  }, [catalogue.produits, catalogueRubriques, searchQuery, categorieFilter, sousCategorieFilter]);

  const isMenuMode = affichageCatalogue === AFFICHAGE_CATALOGUE.menu;
  const hasSearch = Boolean(searchQuery.trim());
  const showMenuCategories =
    isMenuMode &&
    catalogueTab === 'produits' &&
    !hasSearch &&
    categorieFilter === 'TOUS' &&
    !menuShowAll;
  const showMenuSubcategories =
    isMenuMode &&
    catalogueTab === 'produits' &&
    !hasSearch &&
    categorieFilter !== 'TOUS' &&
    sousCategories.length > 0 &&
    sousCategorieFilter === 'TOUS';

  const setAffichageCataloguePersisted = useCallback((mode) => {
    setAffichageCatalogue(mode);
    try {
      localStorage.setItem(CAISSE_AFFICHAGE_KEY, mode);
    } catch { /* */ }
    if (mode === AFFICHAGE_CATALOGUE.menu) {
      setCategorieFilter('TOUS');
      setSousCategorieFilter('TOUS');
      setMenuShowAll(false);
    }
  }, []);

  const menuNavigateBack = useCallback(() => {
    if (menuShowAll) {
      setMenuShowAll(false);
      return;
    }
    if (sousCategorieFilter !== 'TOUS' && sousCategorieFilter !== '__ALL__') {
      setSousCategorieFilter('TOUS');
      return;
    }
    if (sousCategorieFilter === '__ALL__') {
      setSousCategorieFilter('TOUS');
      return;
    }
    setCategorieFilter('TOUS');
  }, [menuShowAll, sousCategorieFilter]);

  const menuBreadcrumb = useMemo(() => {
    if (!isMenuMode || showMenuCategories || hasSearch) return null;
    if (menuShowAll) return 'Tous les produits';
    const parts = [];
    if (categorieFilter !== 'TOUS') parts.push(categorieFilter);
    if (sousCategorieFilter !== 'TOUS' && sousCategorieFilter !== '__ALL__') {
      parts.push(sousCategorieFilter);
    }
    return parts.length ? parts.join(' › ') : null;
  }, [
    isMenuMode,
    showMenuCategories,
    hasSearch,
    menuShowAll,
    categorieFilter,
    sousCategorieFilter,
  ]);

  const prestationsFiltrees = useMemo(() => {
    const q = searchQuery.trim();
    return (catalogue.prestations || []).filter((p) => {
      if (!q) return true;
      return matchSearchText(p.nom, q);
    });
  }, [catalogue.prestations, searchQuery]);

  const aEncaisserCount = aEncaisser.filter((i) => i.actions?.includes('encaisser')).length;

  const loadSession = useCallback(async () => {
    if (!commercantId) {
      setError('Aucun commerce sélectionné.');
      setLoading(false);
      return null;
    }
    try {
      const data = await apiService.getCaisseSession(commercantId);
      setSession(data.session || null);
      setSessionStats(data.stats || null);
      setSessionRecap(data.recap || null);
      if (data.devise) setDevise(data.devise);
      setError('');
      return data;
    } catch (e) {
      if (e.message?.includes('MODULE_CAISSE') || e.message?.includes('403')) {
        setError(
          'Le module Caisse n\'est pas activé. Cochez « Caisse (POS) » dans Paramètres ou Modules activés.'
        );
      } else {
        setError(e.message || 'Erreur chargement caisse');
      }
      return null;
    }
  }, [commercantId]);

  const loadAEncaisser = useCallback(async () => {
    if (!commercantId) return;
    try {
      const data = await apiService.getCaisseAEncaisser(commercantId);
      setAEncaisser(data.items || []);
      if (data.devise) setDevise(data.devise);
    } catch (e) {
      console.error(e);
    }
  }, [commercantId]);

  const loadHistorique = useCallback(async () => {
    if (!commercantId) return;
    try {
      const data = await apiService.getCaisseHistorique(commercantId);
      setHistorique(data.commandes || []);
    } catch (e) {
      console.error(e);
    }
  }, [commercantId]);

  const resetPromo = () => {
    setCodePromo('');
    setPromoReduction(0);
    setPromoMessage('');
    setPromoValide(false);
  };

  const handleVerifierPromo = async () => {
    if (!codePromo.trim()) return;
    setPromoLoading(true);
    try {
      const base = encaissementTarget?.montant ?? totalPanier;
      const res = await apiService.verifierPromoCaisse(
        codePromo.trim(),
        base,
        commercantId
      );
      if (res.valide) {
        setPromoReduction(res.reduction || 0);
        setPromoValide(true);
        setPromoMessage(`Réduction appliquée : −${fmt(res.reduction)}`);
        setPaymentError('');
      } else {
        setPromoReduction(0);
        setPromoValide(false);
        setPromoMessage(res.message || 'Code invalide');
      }
    } catch (e) {
      setPromoReduction(0);
      setPromoValide(false);
      setPromoMessage(e.message || 'Code invalide');
    } finally {
      setPromoLoading(false);
    }
  };

  const loadCatalogue = useCallback(async () => {
    if (!commercantId) return;
    try {
      const [data, rubTree] = await Promise.all([
        apiService.getCaisseCatalogue(commercantId),
        apiService.getCatalogueRubriques(commercantId, false).catch(() => ({ categories: [] })),
      ]);
      const produits = Array.isArray(data?.produits) ? data.produits : [];
      setCatalogue({
        produits,
        prestations: data?.prestations ?? [],
      });
      setCatalogueRubriques(rubTree?.categories || []);
      setModulePrestations(Boolean(data?.modulePrestations));
      if (produits.length === 0 && !data?.prestations?.length && data?.moduleProduits === false) {
        setError('Le module Produits n\'est pas activé pour ce commerce.');
      }
    } catch (e) {
      console.error('[Caisse] catalogue:', e);
      setError(e.message || 'Impossible de charger le catalogue');
      setCatalogue({ produits: [], prestations: [] });
      setCatalogueRubriques([]);
    }
  }, [commercantId]);

  useEffect(() => {
    if (commercantId && !searchParams.get('commercantId')) {
      setSearchParams({ commercantId }, { replace: true });
    }
  }, [commercantId, searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSession();
      setLoading(false);
    })();
  }, [loadSession]);

  const loadTicketsOuverts = useCallback(async () => {
    try {
      const data = await apiService.getTicketsOuverts(commercantId);
      setTicketsOuverts(data?.tickets || []);
    } catch { setTicketsOuverts([]); }
  }, [commercantId]);

  useEffect(() => {
    if (session && view === MODES.accueil) {
      loadAEncaisser();
      loadTicketsOuverts();
    }
  }, [session, view, loadAEncaisser, loadTicketsOuverts]);

  useEffect(() => {
    if (session && view === MODES.vendre && commercantId) {
      loadCatalogue();
    }
  }, [session, view, commercantId, loadCatalogue]);

  useEffect(() => {
    if (session && view === MODES.cloture) {
      loadSession();
    }
  }, [session, view, loadSession]);

  useEffect(() => {
    if (session && view === MODES.historique) {
      loadHistorique();
    }
  }, [session, view, loadHistorique]);

  const lineFromProduit = (produit, extra = {}) => {
    const options = extra.options || {};
    const lineKey =
      extra.poidsSouhaite != null
        ? `${produit.id}::poids::${extra.poidsSouhaite}::${extra.typeDecoupe || ''}::${buildLineKey(produit.id, options)}`
        : buildLineKey(produit.id, options);
    return {
      lineKey,
      lineType: 'produit',
      produitId: produit.id,
      nom: produit.nom,
      prixUnitaire: extra.prixUnitaire ?? produit.prixPromo ?? produit.prix,
      quantite: extra.quantite ?? 1,
      options,
      optionsLabel: extra.optionsLabel || '',
      poidsSouhaite: extra.poidsSouhaite,
      typeDecoupe: extra.typeDecoupe,
      imageUrl: getProductImageUrl(produit),
      categorie: produit.categorie,
    };
  };

  const lineFromPrestation = (prestation, extra = {}) => ({
    lineKey: `prestation::${prestation.id}`,
    lineType: 'prestation',
    prestationId: prestation.id,
    nom: prestation.nom,
    prixUnitaire: extra.prixUnitaire ?? prestation.prix,
    quantite: extra.quantite ?? 1,
    options: {},
    optionsLabel: '',
    imageUrl: prestation.images?.[0] || null,
    categorie: 'Prestation',
  });

  const ajouterPrestationAuPanier = (prestation, extra = {}) => {
    const line = lineFromPrestation(prestation, extra);
    setPanier((prev) => {
      const idx = prev.findIndex((p) => p.lineKey === line.lineKey);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantite: next[idx].quantite + (extra.quantite ?? 1) };
        return next;
      }
      return [...prev, line];
    });
  };

  const ajouterAuPanier = (produit, extra = {}) => {
    const line = lineFromProduit(produit, extra);
    setPanier((prev) => {
      const idx = prev.findIndex((p) => p.lineKey === line.lineKey);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantite: next[idx].quantite + (extra.quantite ?? 1),
        };
        return next;
      }
      return [...prev, line];
    });
  };

  const modifierQuantite = (lineKey, delta) => {
    setPanier((prev) =>
      prev
        .map((l) =>
          l.lineKey === lineKey ? { ...l, quantite: Math.max(0, l.quantite + delta) } : l
        )
        .filter((l) => l.quantite > 0)
    );
  };

  const chargerProduitCaisse = async (produit) => {
    const loaders = [
      () => apiService.getCaisseProduit(produit.id, commercantId),
      () => apiService.getProduitOptions(produit.id),
      () =>
        apiService.request(`/produits/${produit.id}?_=${Date.now()}`, {
          headers: { 'X-Caisse-POS': '1' },
        }),
    ];

    let merged = produit;
    for (const load of loaders) {
      try {
        const res = await load();
        const raw = res?.produit ?? res;
        if (raw?.id) {
          merged = mergeProduitAvecOptions(merged, raw);
        }
      } catch (err) {
        console.warn('[Caisse] chargement produit:', err?.message || err);
      }
    }

    return merged;
  };

  const handleProductTap = async (produit) => {
    if (produit.indisponible || produit.desactiveParIngredient) return;

    if (hasMenuOptions(produit)) {
      setOptionsProduct(produit);
    }

    setLoadingProductId(produit.id);
    try {
      const data = await chargerProduitCaisse(produit);

      setOptionsProduct(null);
      setSelectedProduct(null);

      if (isVenteAuPoids(data)) {
        setPoidsProduct(data);
        return;
      }

      if (hasMenuOptions(data)) {
        setOptionsProduct(data);
        return;
      }

      if (productNeedsCustomization(data)) {
        setSelectedProduct(data);
        return;
      }

      ajouterAuPanier(produit);
    } catch (e) {
      setError(e.message || 'Impossible de charger le produit');
      if (!hasMenuOptions(produit)) {
        ajouterAuPanier(produit);
      }
    } finally {
      setLoadingProductId(null);
    }
  };

  const handleOuvrirSession = async () => {
    setProcessing(true);
    try {
      const data = await apiService.ouvrirCaisseSession(Number(fondCaisse) || 0, commercantId);
      setSession(data.session);
      setView(MODES.accueil);
    } catch (e) {
      setError(e.message || 'Impossible d\'ouvrir la caisse');
    } finally {
      setProcessing(false);
    }
  };

  const mettreEnAttente = async () => {
    if (panier.length === 0) return;
    setProcessing(true);
    try {
      const fideliteFields = buildTicketFideliteFields({
        clientUserId: selectedClient?.id || null,
        confirmedRewardId: fidelite.confirmedRewardId,
      });
      const payload = {
        commercantId,
        produits: panier
          .filter((l) => l.lineType !== 'prestation')
          .map(serializeLigneProduitPourVente),
        prestations: panier
          .filter((l) => l.lineType === 'prestation')
          .map((l) => ({ prestationId: l.prestationId, quantite: l.quantite, prixUnitaire: l.prixUnitaire })),
        contactNom: contactNom || undefined,
        encaisserImmediatement: false,
        ...fideliteFields,
      };
      await apiService.creerTicket(payload);
      setPanier([]);
      setActiveTicketId(null);
      setContactNom('');
      setSelectedClient(null);
      fidelite.clearAll();
      resetPromo();
      await loadTicketsOuverts();
      await loadSession();
      showToast(
        fideliteFields.rewardId
          ? 'Ticket mis en attente (intention récompense enregistrée)'
          : 'Ticket mis en attente'
      );
      setView(MODES.accueil);
    } catch (e) {
      setError(e.message || 'Erreur mise en attente');
    } finally {
      setProcessing(false);
    }
  };

  const reprendreTicket = async (ticket) => {
    const lignes = (ticket.produits || []).map((lp) => ({
      lineKey: `${lp.produitId}::ticket::${lp.id}`,
      lineType: 'produit',
      produitId: lp.produitId,
      nom: lp.produit?.nom || 'Produit',
      prixUnitaire: lp.prixUnitaire,
      quantite: lp.quantite,
      options: {},
      optionsLabel: lp.commentaire || '',
      poidsSouhaite: lp.poidsSouhaite,
      typeDecoupe: lp.typeDecoupe,
    }));
    setPanier(lignes);
    setActiveTicketId(ticket.id);
    setView(MODES.vendre);
    loadCatalogue();

    // F2.4b1 — hydrater client + intention (0 apply)
    try {
      const detail = await apiService.getTicketDetail(ticket.id, commercantId);
      const t = detail?.ticket || ticket;
      const intentRewardId = detail?.rewardIdIntent || null;
      const user = t.user || null;
      const userId = t.userId || user?.id || null;
      if (userId && user) {
        setSelectedClient({
          id: userId,
          nom: user.nom,
          prenom: user.prenom,
          email: user.email,
          telephone: user.telephone,
        });
        setContactNom(
          [user.prenom, user.nom].filter(Boolean).join(' ') || t.contactNom || ''
        );
      } else if (userId) {
        setSelectedClient({ id: userId, nom: null, prenom: null, email: null, telephone: null });
        setContactNom(t.contactNom || '');
      } else {
        setSelectedClient(null);
        setContactNom(t.contactNom || '');
        fidelite.clearAll();
      }
      // hydrate après set client (catalogue charge via effect)
      if (intentRewardId) {
        // léger délai pour laisser le clientUserId se propager au hook
        setTimeout(() => fidelite.hydrateIntent(intentRewardId), 0);
      } else {
        fidelite.resetSelection();
      }
    } catch {
      setContactNom(ticket.contactNom || '');
    }
  };

  const annulerTicketOuvert = (ticketId) => {
    setConfirmModal({
      title: 'Annuler ce ticket ?',
      message: 'Cette action est irréversible. Le ticket sera supprimé.',
      variant: 'danger',
      confirmLabel: 'Annuler le ticket',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await apiService.annulerTicket(ticketId);
          await loadTicketsOuverts();
          await loadSession();
          if (activeTicketId === ticketId) {
            setPanier([]);
            setActiveTicketId(null);
          }
          showToast('Ticket annulé');
        } catch (e) {
          showToast(e.message || 'Erreur annulation', 'error');
        }
      },
    });
  };

  const voirDetailTicket = async (ticketId) => {
    try {
      const data = await apiService.getTicketDetail(ticketId, commercantId);
      const ticket = data?.ticket || null;
      setTicketDetail(ticket);
      setTicketFideliteDisplay(
        data?.fideliteDisplay ||
          (ticket
            ? buildCaisseFideliteDisplay({
                commande: ticket,
                rewardIdIntent: data?.rewardIdIntent || null,
                consommation: ticket.recompenseConsommations?.[0] || null,
              })
            : null)
      );
      setShowTicketDetail(true);
    } catch (e) {
      setError(e.message || 'Erreur chargement détail');
    }
  };

  const annulerTicketPaye = (ticketId) => {
    setAnnulMotif('');
    setConfirmModal({
      title: 'Annuler ce ticket payé ?',
      message: 'INPUT_MOTIF',
      variant: 'danger',
      confirmLabel: 'Confirmer l\'annulation',
      onConfirm: async () => {
        const motif = annulMotif || 'Annulation POS';
        setConfirmModal(null);
        try {
          await apiService.annulerTicketPaye(ticketId, motif);
          setShowTicketDetail(false);
          setTicketDetail(null);
          await loadSession();
          await loadTicketsOuverts();
          showToast('Ticket annulé');
        } catch (e) {
          showToast(e.message || 'Erreur annulation', 'error');
        }
      },
    });
  };

  const ouvrirSavModal = () => {
    setSavTab('articles');
    setSavLineQty({});
    setSavMontantLibre('');
    setSavMotif('');
    setSavMethode('ESPECES');
    setShowSavModal(true);
  };

  const handleRemboursement = async () => {
    if (savLoading) return;
    setSavLoading(true);
    try {
      let payload;
      if (savTab === 'complet') {
        payload = { type: 'COMPLET', motif: savMotif, methodePaiement: savMethode };
      } else if (savTab === 'articles') {
        const activeLines = Object.entries(savLineQty).filter(([, q]) => q > 0);
        if (activeLines.length === 0) {
          showToast('Sélectionnez au moins un article', 'error');
          setSavLoading(false);
          return;
        }
        const ligneQuantites = Object.fromEntries(activeLines);
        payload = { type: 'ARTICLES', ligneIds: activeLines.map(([id]) => id), ligneQuantites, motif: savMotif, methodePaiement: savMethode };
      } else {
        const montant = parseFloat(savMontantLibre);
        if (!montant || montant <= 0) {
          showToast('Montant invalide', 'error');
          setSavLoading(false);
          return;
        }
        payload = { type: 'LIBRE', montant, motif: savMotif, methodePaiement: savMethode };
      }
      const result = await apiService.rembourserTicket(ticketDetail.id, payload);
      showToast(`Remboursé ${fmt(result.montantRembourse)} — solde restant ${fmt(result.soldeRestant)}`);
      setShowSavModal(false);
      const refreshed = await apiService.getTicketDetail(ticketDetail.id);
      setTicketDetail(refreshed?.ticket || refreshed);
      setTicketFideliteDisplay(refreshed?.fideliteDisplay || null);
      await loadSession();
    } catch (e) {
      showToast(e.message || 'Erreur remboursement', 'error');
    } finally {
      setSavLoading(false);
    }
  };

  const imprimerDuplicata = (ticket, displayOverride = null) => {
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) return;
    const display =
      displayOverride ||
      ticketFideliteDisplay ||
      buildCaisseFideliteDisplay({
        commande: ticket,
        rewardIdIntent: null,
        consommation: ticket?.recompenseConsommations?.[0] || null,
      });
    const fideliteBlock = formatFideliteDuplicataBlock(display);
    const lignes = (ticket.produits || [])
      .map((lp) => `<tr><td>${lp.produit?.nom || 'Article'}</td><td style="text-align:center">x${lp.quantite}</td><td style="text-align:right">${fmt(lp.prixTotal || lp.prixUnitaire * lp.quantite)}</td></tr>`)
      .join('');
    const paiements = (ticket.paiements || [])
      .map((p) => `<div style="display:flex;justify-content:space-between"><span>${p.methodePaiement}</span><span>${fmt(p.montant)}</span></div>`)
      .join('');
    w.document.write(`<!DOCTYPE html><html><head><title>Ticket ${ticket.numeroTicket || ticket.numero}</title>
      <style>body{font-family:monospace;max-width:350px;margin:0 auto;padding:20px;font-size:13px}
      h2{text-align:center;margin:0 0 10px}table{width:100%;border-collapse:collapse}
      td{padding:4px 0;border-bottom:1px dashed #ccc}.total{font-size:16px;font-weight:bold;text-align:center;margin:15px 0;border-top:2px solid #000;padding-top:10px}
      .dup{text-align:center;font-weight:bold;color:#c00;margin-bottom:10px}
      @media print{.no-print{display:none}}</style></head>
      <body><p class="dup">--- DUPLICATA ---</p>
      <h2>Ticket ${ticket.numeroTicket || ticket.numero || ''}</h2>
      <p style="text-align:center">${new Date(ticket.dateCommande).toLocaleString('fr-FR')}</p>
      <table>${lignes}</table>
      ${fideliteBlock}
      ${paiements ? '<div style="margin-top:10px;border-top:1px solid #000;padding-top:5px"><strong>Paiements</strong>' + paiements + '</div>' : ''}
      <div class="total">TOTAL : ${fmt(ticket.total)}</div>
      <p style="text-align:center;font-size:11px">${ticket.statut === 'ANNULEE' ? '*** ANNULÉ ***' : 'Merci de votre visite !'}</p>
      <button class="no-print" onclick="window.print()" style="width:100%;padding:10px;margin-top:15px;cursor:pointer;font-size:14px">Imprimer</button>
      </body></html>`);
    w.document.close();
  };

  const handleFermerSession = async (force = false) => {
    setProcessing(true);
    try {
      const res = await apiService.fermerCaisseSession(
        especesComptees !== '' ? Number(especesComptees) : undefined,
        commercantId,
        force
      );
      if (res?.code === 'TICKETS_OUVERTS') {
        setFermetureWarning(res);
        setProcessing(false);
        return;
      }
      setSession(null);
      setSessionStats(null);
      setFermetureWarning(null);
      setView(MODES.accueil);
      setEspecesComptees('');
    } catch (e) {
      if (e.data?.code === 'TICKETS_OUVERTS') {
        setFermetureWarning(e.data);
      } else {
        setError(e.message || 'Erreur clôture');
      }
    } finally {
      setProcessing(false);
    }
  };

  const validerVente = async (check) => {
    if (panier.length === 0 || !check?.ok) return;

    const codeSaisi = codePromo.trim();
    if (codeSaisi && !promoValide) {
      setPaymentError(
        'Code promo non validé. Cliquez sur « OK » ou videz le champ pour continuer sans promo.'
      );
      return;
    }

    // F2.4b2b — payNow et ticket + reward autorisés (apply serveur)
    const payGate = resolveCaisseImmediatePayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      activeTicketId,
    });
    if (payGate.action === 'block') {
      setPaymentError(payGate.message);
      return;
    }
    // F2.5a3 — pas d’encaissement silencieux si reward confirmé mais quote morte
    const rewardGate = resolveCaisseConfirmedRewardPayGate({
      confirmedRewardId: fidelite.confirmedRewardId,
      quote: fidelite.quote,
      quoteLoading: fidelite.quoteLoading,
      quoteError: fidelite.quoteError,
    });
    if (rewardGate.action === 'block') {
      setPaymentError(rewardGate.message);
      return;
    }

    setPaymentError('');
    setProcessing(true);
    if (!paymentIdempotencyKeyRef.current) {
      paymentIdempotencyKeyRef.current = createCaisseIdempotencyKey();
    }
    const idempotencyKey = paymentIdempotencyKeyRef.current;
    try {
      let res;
      if (activeTicketId) {
        // Intention = marker serveur ; sync UI déjà faite via PATCH
        res = await apiService.encaisserCaisse(
          {
            commercantId,
            type: 'COMMANDE',
            id: activeTicketId,
            methodePaiement:
              check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
            montantRecu: check.montantRecu ?? undefined,
            paiements: check.paiements,
            codePromo: promoValide && codeSaisi ? codeSaisi : undefined,
          },
          { idempotencyKey }
        );
      } else {
        const fideliteFields = buildImmediateVenteFideliteFields({
          clientUserId: selectedClient?.id || null,
          confirmedRewardId: fidelite.confirmedRewardId,
        });
        res = await apiService.creerVenteCaisse(
          {
            commercantId,
            produits: panier
              .filter((l) => l.lineType !== 'prestation')
              .map(serializeLigneProduitPourVente),
            prestations: panier
              .filter((l) => l.lineType === 'prestation')
              .map((l) => ({
                prestationId: l.prestationId,
                quantite: l.quantite,
                prixUnitaire: l.prixUnitaire,
              })),
            contactNom: contactNom || undefined,
            codePromo: promoValide && codeSaisi ? codeSaisi : undefined,
            encaisserImmediatement: true,
            methodePaiement:
              check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
            montantRecu: check.montantRecu != null ? Number(check.montantRecu) : undefined,
            paiements: check.paiements,
            ...fideliteFields,
          },
          { idempotencyKey }
        );
      }
      paymentIdempotencyKeyRef.current = null;
      setPanier([]);
      setActiveTicketId(null);
      setContactNom('');
      setSelectedClient(null);
      fidelite.clearAll();
      resetPaiement();
      setPaymentMode(null);
      resetPromo();
      await loadSession();
      await loadTicketsOuverts();
      let msg = 'Vente enregistrée';
      if (res.rendu != null && res.rendu > 0) msg += ` · Rendu : ${fmt(res.rendu)}`;
      if (res.reduction > 0) msg += ` · Réduction : ${fmt(res.reduction)}`;
      if (res.reductionAvantage > 0) msg += ` · Fidélité : ${fmt(res.reductionAvantage)}`;
      showToast(msg);
      setView(MODES.accueil);
    } catch (e) {
      const detail = e.data?.details?.[0]?.message || e.data?.message;
      const msg = detail || e.message || 'Erreur lors de la vente';
      setPaymentError(msg);
      // Soft-skip interdit : conserver panier / client / intention si erreur catalogue
      if (!isRewardCatalogueVenteError(e) && !isRewardCatalogueVenteError({ code: e.data?.code })) {
        // autres erreurs : panier aussi conservé (pas de clear)
      }
    } finally {
      setProcessing(false);
    }
  };

  const confirmerEncaissement = async (check) => {
    if (!encaissementTarget || !check?.ok) return;

    setPaymentError('');
    setProcessing(true);
    if (!paymentIdempotencyKeyRef.current) {
      paymentIdempotencyKeyRef.current = createCaisseIdempotencyKey();
    }
    const idempotencyKey = paymentIdempotencyKeyRef.current;
    try {
      const res = await apiService.encaisserCaisse(
        {
          commercantId,
          type: encaissementTarget.type,
          id: encaissementTarget.id,
          methodePaiement:
            check.methodePaiement === 'MIXTE' ? 'ESPECES' : check.methodePaiement,
          montantRecu: check.montantRecu ?? undefined,
          paiements: check.paiements,
        },
        encaissementTarget.type === 'COMMANDE' ? { idempotencyKey } : undefined
      );
      paymentIdempotencyKeyRef.current = null;
      setEncaissementTarget(null);
      resetPaiement();
      await loadAEncaisser();
      await loadSession();
      let msg = 'Encaissé';
      if (res.rendu != null && res.rendu > 0) msg += ` · Rendu : ${fmt(res.rendu)}`;
      showToast(msg);
    } catch (e) {
      const detail = e.data?.details?.[0]?.message || e.data?.message;
      const msg = detail || e.message || 'Erreur encaissement';
      setPaymentError(msg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3">
        <RefreshCw className="h-10 w-10 animate-spin text-emerald-600" />
        <p className="text-slate-500 text-sm">Chargement de la caisse…</p>
      </div>
    );
  }

  if (!commercantId) {
    return (
      <div className="max-w-lg mx-auto mt-12 p-10 bg-white rounded-3xl shadow-xl text-center border border-slate-100">
        <Banknote className="h-14 w-14 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Caisse (POS)</h2>
        <p className="text-slate-600 mt-2 text-sm leading-relaxed">
          Commerce non identifié. Ouvrez la caisse depuis la fiche commerçant (bouton « Ouvrir la
          caisse ») ou vérifiez que votre compte est bien lié à un commerce.
        </p>
        <Link
          to="/commercants"
          className="inline-flex mt-6 items-center gap-2 text-emerald-600 font-semibold hover:underline"
        >
          Voir les commerçants →
        </Link>
      </div>
    );
  }

  if (error && !session && error.includes('module')) {
    return (
      <div className="max-w-lg mx-auto mt-12 p-10 bg-white rounded-3xl shadow-xl text-center">
        <Banknote className="h-14 w-14 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-bold">Module non activé</h2>
        <p className="text-slate-600 mt-2 text-sm">{error}</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 px-8 py-10 text-center text-white">
            <Banknote className="h-16 w-16 mx-auto mb-4 opacity-90" />
            <h1 className="text-2xl font-bold">Ouvrir la caisse</h1>
            <p className="text-emerald-100 mt-2 text-sm">Démarrez votre session de vente</p>
          </div>
          <div className="p-8">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Fond de caisse ({devise})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={fondCaisse}
              onChange={(e) => setFondCaisse(e.target.value)}
              onFocus={(e) => e.target.select()}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-xl focus:border-emerald-500 outline-none"
            />
            {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            <button
              type="button"
              onClick={handleOuvrirSession}
              disabled={processing}
              className="w-full mt-6 py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-50 shadow-lg shadow-emerald-600/30"
            >
              Ouvrir la caisse
            </button>
          </div>
        </div>
      </div>
    );
  }

  const paymentOpen = !!encaissementTarget || paymentMode === 'vente';
  const rewardReductionEstimee =
    fidelite.confirmedRewardId &&
    fidelite.quote?.applicable
      ? Number(fidelite.quote.reductionAvantage) || 0
      : 0;
  const paymentMontant =
    encaissementTarget?.montant ?? (paymentMode === 'vente' ? totalPanier : 0);
  const paymentTitle = encaissementTarget ? 'Encaisser' : 'Encaisser la vente';
  const paymentSubtitle = encaissementTarget?.label || `${panier.length} article(s)`;
  const paymentReductionTotale =
    paymentMode === 'vente'
      ? Math.round(((Number(promoReduction) || 0) + rewardReductionEstimee) * 100) / 100
      : 0;

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const fondRecap = round2(sessionRecap?.fondDeCaisse ?? 0);
  const especesEnCaisseAttendues = round2(
    sessionRecap?.especesEnCaisse ?? sessionRecap?.totalEspeces ?? fondRecap
  );
  /** Fallback si l'API n'envoie pas encore ventesEspeces (ancien cache) */
  const ventesEspecesAffichees = round2(
    sessionRecap?.ventesEspeces ??
      Math.max(0, especesEnCaisseAttendues - fondRecap)
  );
  const ecartPreview =
    especesComptees !== ''
      ? round2(round2(especesComptees) - round2(especesEnCaisseAttendues))
      : null;
  const ecartAffiche =
    ecartPreview != null && Math.abs(ecartPreview) < 0.005 ? 0 : ecartPreview;

  return (
    <div className="min-h-[calc(100vh-6rem)] -mx-4 sm:-mx-6 lg:-mx-8">
      <CaisseToast toasts={toasts} onDismiss={dismissToast} />

      <CaisseConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title || ''}
        confirmLabel={confirmModal?.confirmLabel}
        cancelLabel="Retour"
        variant={confirmModal?.variant}
        processing={processing}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      >
        {confirmModal?.message === 'INPUT_MOTIF' ? (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Le ticket sera annulé et les paiements marqués comme remboursés.
            </p>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Motif d&apos;annulation
            </label>
            <input
              type="text"
              value={annulMotif}
              onChange={(e) => setAnnulMotif(e.target.value)}
              placeholder="Ex: erreur de saisie, demande client..."
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-red-400 outline-none"
              autoFocus
            />
          </div>
        ) : (
          <p className="text-sm text-slate-600">{confirmModal?.message}</p>
        )}
      </CaisseConfirmModal>

      {showClotureConfirm && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowClotureConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-800 px-6 py-5 rounded-t-2xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Confirmer la fermeture
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Chiffre d&apos;affaires</span>
                  <span className="font-bold text-slate-900">{fmt(sessionRecap?.totalVentes ?? 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Nombre de ventes</span>
                  <span className="font-bold">{sessionRecap?.ventesCount ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Espèces attendues</span>
                  <span className="font-bold">{fmt(especesEnCaisseAttendues)}</span>
                </div>
                {ecartAffiche != null && !Number.isNaN(ecartAffiche) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Écart caisse</span>
                    <span className={`font-bold ${ecartAffiche === 0 ? 'text-emerald-600' : ecartAffiche > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                      {ecartAffiche >= 0 ? '+' : ''}{fmt(ecartAffiche)}
                    </span>
                  </div>
                )}
              </div>
              {sessionRecap?.remboursements?.count > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-1.5">
                    <RotateCcw className="h-4 w-4" /> Remboursements
                  </h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Nombre</span>
                    <span className="font-bold text-orange-800">{sessionRecap.remboursements.count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-orange-600">Total remboursé</span>
                    <span className="font-bold text-orange-800">-{fmt(sessionRecap.remboursements.total)}</span>
                  </div>
                  {sessionRecap.remboursements.especes > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont espèces</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.especes)}</span>
                    </div>
                  )}
                  {sessionRecap.remboursements.carte > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-600">dont carte</span>
                      <span className="font-bold">-{fmt(sessionRecap.remboursements.carte)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-orange-200 pt-2">
                    <span className="text-orange-700 font-semibold">CA net</span>
                    <span className="font-bold text-orange-900">{fmt(sessionRecap.totalNet ?? sessionRecap.totalVentes)}</span>
                  </div>
                </div>
              )}
              <p className="text-sm text-red-600 font-medium">
                Cette action est définitive. Vous ne pourrez plus accéder à cette session.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button
                type="button"
                onClick={() => setShowClotureConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowClotureConfirm(false);
                  handleFermerSession(false);
                }}
                disabled={processing}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                Fermer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      <CaissePaymentModal
        open={paymentOpen}
        title={paymentTitle}
        subtitle={paymentSubtitle}
        montant={paymentMontant}
        reduction={paymentReductionTotale}
        fmt={fmt}
        showCodePromo={paymentMode === 'vente'}
        codePromo={codePromo}
        setCodePromo={(value) => {
          setCodePromo(value);
          setPromoValide(false);
          setPromoReduction(0);
          setPromoMessage('');
        }}
        onVerifierPromo={paymentMode === 'vente' ? handleVerifierPromo : undefined}
        promoMessage={promoMessage}
        promoLoading={promoLoading}
        error={paymentError}
        onClose={() => {
          setEncaissementTarget(null);
          setPaymentMode(null);
          resetPaiement();
          resetPromo();
        }}
        onConfirm={(check) => {
          if (encaissementTarget) confirmerEncaissement(check);
          else validerVente(check);
        }}
        processing={processing}
      />

      <CaisseRecompenseConfirmDialog
        open={fidelite.confirmOpen}
        clientLabel={fidelite.client ? [fidelite.client.prenom, fidelite.client.nom].filter(Boolean).join(' ') : null}
        reward={fidelite.pendingReward}
        quote={fidelite.quote}
        message={fidelite.confirmMessage}
        totalApres={
          fidelite.confirmOpen && fidelite.quote?.applicable
            ? estimateCaissePayableTotal({
                sousTotal: totalPanier,
                reductionPromo: Number(promoReduction) || 0,
                reductionAvantage: Number(fidelite.quote.reductionAvantage) || 0,
              })
            : null
        }
        onConfirm={fidelite.acceptConfirm}
        onCancel={fidelite.cancelConfirm}
        busy={fidelite.quoteLoading}
      />

      <CaisseProductModal
        produit={selectedProduct}
        fmt={fmt}
        quantitePanier={selectedProduct ? panierQtyMap[selectedProduct.id] || 0 : 0}
        onClose={() => setSelectedProduct(null)}
        onAdd={() => {
          if (!selectedProduct) return;
          if (hasMenuOptions(selectedProduct) || productNeedsCustomization(selectedProduct)) {
            handleProductTap(selectedProduct);
          } else {
            ajouterAuPanier(selectedProduct);
            setSelectedProduct(null);
          }
        }}
        onSetQuantite={(delta) => {
          if (!selectedProduct) return;
          const lines = panier.filter((l) => l.produitId === selectedProduct.id);
          if (lines.length === 0 && delta > 0) {
            handleProductTap(selectedProduct);
            return;
          }
          if (lines.length === 1) modifierQuantite(lines[0].lineKey, delta);
        }}
      />

      <CaisseOptionsModal
        produit={optionsProduct}
        fmt={fmt}
        onClose={() => setOptionsProduct(null)}
        onAddToPanier={({ produit, quantite, prixUnitaire, options, optionsLabel }) => {
          ajouterAuPanier(produit, { quantite, prixUnitaire, options, optionsLabel });
        }}
      />

      {poidsProduct && (
        <CaissePoidsModal
          key={poidsProduct.id}
          produit={poidsProduct}
          fmt={fmt}
          onClose={() => setPoidsProduct(null)}
          onConfirm={({ poidsSouhaite, typeDecoupe, quantite, prixUnitaire }) => {
            ajouterAuPanier(poidsProduct, {
              poidsSouhaite,
              typeDecoupe,
              quantite,
              prixUnitaire,
            });
            setPoidsProduct(null);
          }}
        />
      )}

      {view === MODES.vendre && (
        <div className="flex h-[calc(100vh-5rem)] bg-slate-50">
          <div className="flex-1 flex flex-col min-w-0 border-r border-slate-200">
            <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    setView(MODES.accueil);
                    setSearchQuery('');
                  }}
                  className="p-2 rounded-xl hover:bg-slate-100 text-slate-600"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-slate-900">Nouvelle vente</h2>
                  <p className="text-xs text-slate-500">
                    {catalogueTab === 'produits'
                      ? `${produitsFiltres.length} produits`
                      : `${prestationsFiltrees.length} prestations`}
                  </p>
                </div>
                {catalogueTab === 'produits' && (
                  <div
                    className="flex rounded-xl border border-slate-200 p-0.5 bg-slate-50 shrink-0"
                    title="Mode d'affichage du catalogue"
                  >
                    <button
                      type="button"
                      onClick={() => setAffichageCataloguePersisted(AFFICHAGE_CATALOGUE.compact)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                        affichageCatalogue === AFFICHAGE_CATALOGUE.compact
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <List className="h-3.5 w-3.5" />
                      Compact
                    </button>
                    <button
                      type="button"
                      onClick={() => setAffichageCataloguePersisted(AFFICHAGE_CATALOGUE.menu)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                        affichageCatalogue === AFFICHAGE_CATALOGUE.menu
                          ? 'bg-white text-emerald-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <LayoutGrid className="h-3.5 w-3.5" />
                      Menu
                    </button>
                  </div>
                )}
              </div>
              {(modulePrestations && catalogue.prestations?.length > 0) && (
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setCatalogueTab('produits')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold ${
                      catalogueTab === 'produits'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Produits
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogueTab('prestations')}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold ${
                      catalogueTab === 'prestations'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Prestations
                  </button>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="search"
                  placeholder={
                    catalogueTab === 'prestations' ? 'Rechercher une prestation…' : 'Rechercher un produit…'
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-500 outline-none text-sm"
                />
              </div>
              {catalogueTab === 'produits' && affichageCatalogue === AFFICHAGE_CATALOGUE.compact && categories.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategorieFilter(cat)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        categorieFilter === cat
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat === 'TOUS' ? 'Tous' : cat}
                    </button>
                  ))}
                </div>
              )}
              {catalogueTab === 'produits' && affichageCatalogue === AFFICHAGE_CATALOGUE.compact && sousCategories.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1 scrollbar-hide">
                  <button
                    type="button"
                    onClick={() => setSousCategorieFilter('TOUS')}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      sousCategorieFilter === 'TOUS'
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    Toute la catégorie
                  </button>
                  {sousCategories.map((sub) => (
                    <button
                      key={sub}
                      type="button"
                      onClick={() => setSousCategorieFilter(sub)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        sousCategorieFilter === sub
                          ? 'bg-slate-700 text-white'
                          : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              )}
            </header>

            <div className="flex-1 overflow-y-auto p-4">
              {catalogueTab === 'prestations' ? (
                prestationsFiltrees.length === 0 ? (
                  <p className="text-center text-slate-500 py-16">Aucune prestation</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {prestationsFiltrees.map((pr) => (
                      <button
                        key={pr.id}
                        type="button"
                        onClick={() => ajouterPrestationAuPanier(pr)}
                        className="text-left bg-white rounded-2xl border-2 border-slate-100 p-4 hover:border-emerald-400 hover:shadow-lg transition"
                      >
                        <p className="font-semibold text-slate-900 line-clamp-2">{pr.nom}</p>
                        {pr.dureeMinutes && (
                          <p className="text-xs text-slate-500 mt-1">{pr.dureeMinutes} min</p>
                        )}
                        <p className="text-lg font-bold text-emerald-600 mt-2">{fmt(pr.prix)}</p>
                      </button>
                    ))}
                  </div>
                )
              ) : showMenuCategories ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Rubriques</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                    <MenuNavTile
                      label="Tous les produits"
                      count={catalogue.produits.length}
                      previewImages={rubriqueCoverImages.previewImages}
                      paletteIndex={0}
                      onClick={() => setMenuShowAll(true)}
                    />
                    {categoryMenuItems.map((cat, index) => (
                      <MenuNavTile
                        key={cat}
                        label={cat}
                        count={produitCountMaps.byCategorie.get(rubriqueCountKey(cat)) || 0}
                        imageUrl={rubriqueCoverImages.byCategorie.get(rubriqueCountKey(cat))}
                        paletteIndex={index + 1}
                        onClick={() => {
                          setMenuShowAll(false);
                          setCategorieFilter(cat);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : showMenuSubcategories ? (
                <div>
                  <button
                    type="button"
                    onClick={menuNavigateBack}
                    className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {categorieFilter}
                  </button>
                  <p className="text-xs text-slate-500 mb-2">Sous-rubriques</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                    <MenuNavTile
                      label="Toute la catégorie"
                      count={
                        produitCountMaps.byCategorie.get(rubriqueCountKey(categorieFilter)) || 0
                      }
                      imageUrl={rubriqueCoverImages.byCategorie.get(rubriqueCountKey(categorieFilter))}
                      paletteIndex={Math.max(0, categoryMenuItems.indexOf(categorieFilter)) + 1}
                      onClick={() => setSousCategorieFilter('__ALL__')}
                    />
                    {sousCategories.map((sub, index) => (
                      <MenuNavTile
                        key={sub}
                        label={sub}
                        count={
                          produitCountMaps.bySousCategorie.get(
                            `${rubriqueCountKey(categorieFilter)}::${rubriqueCountKey(sub)}`
                          ) || 0
                        }
                        imageUrl={rubriqueCoverImages.bySousCategorie.get(
                          `${rubriqueCountKey(categorieFilter)}::${rubriqueCountKey(sub)}`
                        )}
                        paletteIndex={index + 2}
                        onClick={() => setSousCategorieFilter(sub)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {menuBreadcrumb && (
                    <button
                      type="button"
                      onClick={menuNavigateBack}
                      className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {menuBreadcrumb}
                    </button>
                  )}
                  {produitsFiltres.length === 0 ? (
                    <p className="text-center text-slate-500 py-16">Aucun produit trouvé</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {produitsFiltres.map((p) => (
                        <ProductTile
                          key={p.id}
                          produit={p}
                          fmt={fmt}
                          qty={panierQtyMap[p.id] || 0}
                          onTap={handleProductTap}
                          onInfo={handleProductTap}
                          loading={loadingProductId === p.id}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <aside className="w-full max-w-[22rem] bg-white flex flex-col shadow-xl">
            <div className="px-4 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <h3 className="font-bold text-lg flex items-center gap-2 text-slate-900">
                <ShoppingCart className="h-5 w-5 text-emerald-600" />
                Panier
                {panier.length > 0 && (
                  <span className="ml-auto text-sm font-normal text-slate-500">
                    {panier.reduce((s, l) => s + l.quantite, 0)} art.
                  </span>
                )}
              </h3>
              <input
                type="text"
                placeholder="Nom libre (optionnel, sans fidélité)"
                value={contactNom}
                onChange={(e) => setContactNom(e.target.value)}
                className="mt-3 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-emerald-500 outline-none"
              />
              <div className="mt-3">
                <CaisseClientPicker
                  commercantId={commercantId}
                  selectedClient={selectedClient}
                  disabled={processing}
                  onSelect={(c) => {
                    setSelectedClient(c);
                    const label = [c.prenom, c.nom].filter(Boolean).join(' ');
                    if (label && !contactNom) setContactNom(label);
                  }}
                  onClear={() => {
                    setSelectedClient(null);
                    fidelite.clearAll();
                  }}
                />
              </div>
              {fidelite.showPanel ? (
                <div className="mt-3">
                  <CaisseRecompensePanel
                    solde={fidelite.solde}
                    recompenses={fidelite.recompenses}
                    loading={fidelite.catalogueLoading}
                    error={fidelite.catalogueError}
                    selectedRewardId={fidelite.selectedRewardId}
                    confirmedRewardId={fidelite.confirmedRewardId}
                    quote={fidelite.quote}
                    quoteLoading={fidelite.quoteLoading}
                    quoteError={fidelite.quoteError}
                    notice={fidelite.notice}
                    onKeepPoints={fidelite.selectKeepPoints}
                    onSelectReward={fidelite.requestSelectReward}
                    onDismissNotice={fidelite.dismissNotice}
                    disabled={processing}
                  />
                </div>
              ) : null}
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {panier.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <ShoppingCart className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                  <p className="text-slate-400 text-sm">Panier vide</p>
                  <p className="text-slate-400 text-xs mt-1">Un clic sur un produit pour l&apos;ajouter</p>
                </div>
              ) : (
                panier.map((l) => (
                  <div
                    key={l.lineKey}
                    className="flex gap-3 p-2 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <div className="w-14 h-14 rounded-lg bg-slate-200 overflow-hidden shrink-0">
                      {l.imageUrl ? (
                        <img src={l.imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                          —
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold text-sm text-slate-900 leading-tight min-w-0 pr-1">
                          {l.nom}
                        </p>
                        <p className="text-emerald-600 font-bold text-sm shrink-0 tabular-nums text-right">
                          {fmt(l.prixUnitaire * l.quantite)}
                        </p>
                      </div>
                      <CaissePanierOptions options={l.options} />
                      {l.poidsSouhaite ? (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {l.poidsSouhaite} g{l.typeDecoupe ? ` · ${l.typeDecoupe}` : ''}
                        </p>
                      ) : null}
                      <div className="flex items-center gap-1 mt-1.5">
                        <button
                          type="button"
                          onClick={() => modifierQuantite(l.lineKey, -1)}
                          className="p-1 rounded-md bg-white border border-slate-200"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold">{l.quantite}</span>
                        <button
                          type="button"
                          onClick={() => modifierQuantite(l.lineKey, 1)}
                          className="p-1 rounded-md bg-white border border-slate-200"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => modifierQuantite(l.lineKey, -999)}
                          className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-slate-200 bg-white">
              {activeTicketId && (
                <p className="text-xs text-orange-600 font-semibold mb-2 flex items-center gap-1">
                  <PauseCircle className="h-3.5 w-3.5" />
                  Ticket en cours — repris
                </p>
              )}
              <div className="flex justify-between items-baseline mb-4">
                <span className="text-slate-600 font-medium">Total</span>
                <span className="text-2xl font-bold text-slate-900">{fmt(totalPanier)}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const payGate = resolveCaisseImmediatePayGate({
                    confirmedRewardId: fidelite.confirmedRewardId,
                    activeTicketId,
                  });
                  if (payGate.action === 'block') {
                    showToast(payGate.message, 'error');
                    return;
                  }
                  const rewardGate = resolveCaisseConfirmedRewardPayGate({
                    confirmedRewardId: fidelite.confirmedRewardId,
                    quote: fidelite.quote,
                    quoteLoading: fidelite.quoteLoading,
                    quoteError: fidelite.quoteError,
                  });
                  if (rewardGate.action === 'block') {
                    showToast(rewardGate.message, 'error');
                    return;
                  }
                  resetPaiement();
                  resetPromo();
                  setPaymentMode('vente');
                }}
                disabled={panier.length === 0}
                className="w-full py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-40 shadow-lg shadow-emerald-600/25 text-lg"
              >
                Encaisser
              </button>
              {!activeTicketId && (
                <button
                  type="button"
                  onClick={mettreEnAttente}
                  disabled={panier.length === 0 || processing}
                  className="w-full mt-2 py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  <PauseCircle className="h-5 w-5" />
                  Mettre en attente
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      {view === MODES.encaisser && (
        <div className="max-w-3xl mx-auto p-6">
          <header className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => setView(MODES.accueil)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" /> Retour
            </button>
            <h2 className="text-xl font-bold">À encaisser</h2>
            <button type="button" onClick={loadAEncaisser} className="p-2 rounded-xl hover:bg-slate-100">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
            </button>
          </header>
          {aEncaisser.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
              <Receipt className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-500">Rien à encaisser pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {aEncaisser.map((item) => {
                const Icon = TYPE_ICONS[item.type] || Receipt;
                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-4 shadow-sm hover:shadow-md transition"
                  >
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-bold uppercase text-slate-400">{item.type}</span>
                      <p className="font-semibold text-slate-900">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.client || '—'}</p>
                      {item.lignes?.length > 0 && (
                        <ul className="mt-2 text-xs text-slate-600 space-y-0.5">
                          {item.lignes.slice(0, 4).map((l, i) => (
                            <li key={i}>
                              {l.nom} ×{l.quantite}
                              {l.optionsText ? (
                                <span className="block text-slate-400 pl-2 whitespace-pre-line">
                                  {l.optionsText}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {item.montant != null && (
                        <span className="text-lg font-bold text-slate-900">{fmt(item.montant)}</span>
                      )}
                      {item.actions?.includes('encaisser') && (
                        <button
                          type="button"
                          onClick={() => {
                            setEncaissementTarget(item);
                            resetPaiement();
                          }}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700"
                        >
                          Encaisser
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === MODES.cloture && (
        <div className="max-w-md mx-auto p-6">
          <button
            type="button"
            onClick={() => setView(MODES.accueil)}
            className="flex items-center gap-2 text-slate-600 mb-6"
          >
            <ArrowLeft className="h-5 w-5" /> Retour
          </button>
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="bg-slate-800 px-6 py-5 text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Lock className="h-6 w-6" /> Clôturer la caisse
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
                <div>
                  <p className="text-sm text-slate-600">Chiffre d&apos;affaires (ventes encaissées)</p>
                  <p className="text-2xl font-bold text-slate-900">{fmt(sessionRecap?.totalNet ?? sessionRecap?.totalVentes ?? 0)}</p>
                  <p className="text-xs text-slate-500">{sessionRecap?.ventesCount ?? 0} vente(s){sessionRecap?.remboursements?.count > 0 ? ` · ${sessionRecap.remboursements.count} remb.` : ''}</p>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Répartition des ventes
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-white rounded-lg px-3 py-2">
                      <span className="text-slate-500 text-xs">Espèces (ventes)</span>
                      <p className="font-semibold">{fmt(ventesEspecesAffichees)}</p>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2">
                      <span className="text-slate-500 text-xs">Carte</span>
                      <p className="font-semibold">{fmt(sessionRecap?.totalCarte ?? 0)}</p>
                    </div>
                    {(sessionRecap?.totalCheque > 0 || sessionRecap?.totalVirement > 0) && (
                      <>
                        <div className="bg-white rounded-lg px-3 py-2">
                          <span className="text-slate-500 text-xs">Chèque</span>
                          <p className="font-semibold">{fmt(sessionRecap?.totalCheque ?? 0)}</p>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2">
                          <span className="text-slate-500 text-xs">Virement</span>
                          <p className="font-semibold">{fmt(sessionRecap?.totalVirement ?? 0)}</p>
                        </div>
                      </>
                    )}
                  </div>
                  {sessionRecap?.repartitionOk === false && (
                    <p className="text-xs text-amber-700 mt-2">
                      Écart de répartition : {fmt(sessionRecap?.ecartRepartition ?? 0)} — vérifiez les
                      anciennes ventes sans détail de paiement.
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-2">
                  Contrôle du tiroir espèces
                </p>
                <div className="space-y-1 text-sm text-amber-950">
                  <div className="flex justify-between">
                    <span>Fond de caisse (ouverture)</span>
                    <span className="font-medium">{fmt(fondRecap)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>+ Ventes en espèces</span>
                    <span className="font-medium">{fmt(ventesEspecesAffichees)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-amber-300 font-bold text-base">
                    <span>= Espèces attendues dans le tiroir</span>
                    <span>{fmt(especesEnCaisseAttendues)}</span>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Espèces comptées dans le tiroir
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={especesComptees}
                onChange={(e) => setEspecesComptees(e.target.value)}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v)) setEspecesComptees(String(Math.round(v * 100) / 100));
                }}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 mb-2 focus:border-red-400 outline-none"
              />
              {ecartAffiche != null && !Number.isNaN(ecartAffiche) && (
                <p
                  className={`text-sm font-semibold mb-4 ${
                    ecartAffiche === 0
                      ? 'text-emerald-700'
                      : ecartAffiche > 0
                        ? 'text-amber-700'
                        : 'text-red-600'
                  }`}
                >
                  Écart caisse : {ecartAffiche >= 0 ? '+' : ''}
                  {fmt(ecartAffiche)}
                </p>
              )}
              {fermetureWarning && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
                  <p className="text-red-800 font-bold mb-2">
                    {fermetureWarning.count} ticket(s) ouvert(s) non encaissé(s)
                  </p>
                  <p className="text-sm text-red-700 mb-3">
                    Total en attente : {fmt(fermetureWarning.totalOuvert || 0)}
                  </p>
                  <ul className="text-sm text-red-700 mb-3 space-y-1">
                    {(fermetureWarning.ticketsOuverts || []).map((t) => (
                      <li key={t.id}>
                        Ticket #{t.numero || t.id.slice(-6)} — {fmt(t.total || 0)}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFermetureWarning(null);
                        setView(MODES.accueil);
                      }}
                      className="flex-1 py-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50"
                    >
                      Retour à la caisse
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFermerSession(true)}
                      disabled={processing}
                      className="flex-1 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      Annuler les tickets et fermer
                    </button>
                  </div>
                </div>
              )}
              {!fermetureWarning && (
                <button
                  type="button"
                  onClick={() => setShowClotureConfirm(true)}
                  disabled={processing}
                  className="w-full py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 disabled:opacity-50"
                >
                  Fermer la caisse
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {view === MODES.historique && (
        <div className="max-w-3xl mx-auto p-6">
          <header className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => setView(MODES.accueil)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" /> Retour
            </button>
            <h2 className="text-xl font-bold">Historique session</h2>
            <button type="button" onClick={loadHistorique} className="p-2 rounded-xl hover:bg-slate-100">
              <RefreshCw className="h-5 w-5 text-emerald-600" />
            </button>
          </header>
          {historique.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
              <History className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-500">Aucune vente sur cette session</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historique.map((cmd) => {
                const statutLabel = cmd.statut === 'ANNULEE' ? 'Annulé' : cmd.statut === 'EN_ATTENTE' ? 'En attente' : 'Payé';
                const statutColor = cmd.statut === 'ANNULEE' ? 'bg-red-100 text-red-700' : cmd.statut === 'EN_ATTENTE' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700';
                const refunds = (cmd.paiements || []).filter((p) => p.statutPaiement === 'REMBOURSE' && p.montant < 0);
                const totalRembourse = refunds.reduce((s, p) => s + Math.abs(p.montant), 0);
                const hasRefund = totalRembourse > 0;
                const totalNet = hasRefund ? cmd.total - totalRembourse : cmd.total;
                return (
                  <button
                    key={cmd.id}
                    type="button"
                    onClick={() => voirDetailTicket(cmd.id)}
                    className="w-full text-left bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:border-violet-300 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {cmd.numeroTicket || cmd.numero}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(cmd.dateCommande).toLocaleString('fr-FR')} ·{' '}
                          {cmd.methodePaiement || '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        {hasRefund ? (
                          <>
                            <span className="text-sm text-slate-400 line-through">{fmt(cmd.total)}</span>
                            <span className="text-lg font-bold text-orange-700 ml-1">{fmt(totalNet)}</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold">{fmt(cmd.total)}</span>
                        )}
                        <span className={`block text-xs font-semibold px-2 py-0.5 rounded-full mt-1 ${statutColor}`}>
                          {statutLabel}
                        </span>
                      </div>
                    </div>
                    <ul className="mt-2 text-sm text-slate-600 space-y-1">
                      {(cmd.produits || []).slice(0, 3).map((lp) => (
                        <li key={lp.id}>
                          {lp.produit?.nom} x{lp.quantite}
                        </li>
                      ))}
                      {(cmd.produits || []).length > 3 && (
                        <li className="text-xs text-slate-400">
                          +{cmd.produits.length - 3} autre(s)...
                        </li>
                      )}
                    </ul>
                    <p className="text-xs text-violet-500 mt-2 font-medium">
                      Voir le détail
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === MODES.accueil && (
        <div className="max-w-4xl mx-auto p-6">
          <div className="rounded-3xl overflow-hidden shadow-xl mb-8 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
            <div className="p-8 flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="text-emerald-100 text-sm font-medium uppercase tracking-wider">
                  Session active
                </p>
                <p className="text-4xl sm:text-5xl font-bold mt-2 tracking-tight">
                  {fmt(sessionStats?.totalVentes ?? 0)}
                </p>
                <p className="text-emerald-100/90 mt-2">
                  {sessionStats?.ventesCount ?? 0} vente(s) · Devise {devise}
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-white/10 backdrop-blur">
                <Banknote className="h-14 w-14" />
              </div>
            </div>
          </div>

          {error && (
            <p className="text-red-600 text-sm mb-4 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
              {error}
            </p>
          )}

          {ticketsOuverts.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-orange-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <PauseCircle className="h-4 w-4" />
                Tickets en attente ({ticketsOuverts.length})
              </h3>
              <div className="space-y-2">
                {ticketsOuverts.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        Ticket #{t.numero || t.id.slice(-6)}
                        {(t.hasFideliteIntent || t.rewardIdIntent) && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                            Fidélité
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t.produits?.length || 0} article(s) · {fmt(t.total || 0)}
                        {(t.hasFideliteIntent || t.rewardIdIntent) && (
                          <span className="text-violet-600"> · intention récompense</span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => voirDetailTicket(t.id)}
                      className="p-2 rounded-lg hover:bg-orange-100 text-slate-500"
                      title="Voir le détail"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => reprendreTicket(t)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-1"
                    >
                      <PlayCircle className="h-4 w-4" />
                      Reprendre
                    </button>
                    <button
                      type="button"
                      onClick={() => annulerTicketOuvert(t.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500"
                      title="Annuler"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                setActiveTicketId(null);
                setSelectedClient(null);
                fidelite.clearAll();
                setContactNom('');
                loadCatalogue();
                setView(MODES.vendre);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                <Plus className="h-7 w-7 text-emerald-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Nouvelle vente</h3>
              <p className="text-sm text-slate-500 mt-1">Catalogue avec photos et détails</p>
            </button>

            <button
              type="button"
              onClick={() => {
                loadAEncaisser();
                setView(MODES.encaisser);
              }}
              className="group relative text-left p-6 bg-white rounded-2xl border-2 border-amber-100 hover:border-amber-400 hover:shadow-xl transition-all"
            >
              {aEncaisserCount > 0 && (
                <span className="absolute top-4 right-4 min-w-[1.5rem] h-7 px-2 flex items-center justify-center rounded-full bg-amber-500 text-white text-sm font-bold">
                  {aEncaisserCount}
                </span>
              )}
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                <Clock className="h-7 w-7 text-amber-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">À encaisser</h3>
              <p className="text-sm text-slate-500 mt-1">Commandes web, RDV, réservations</p>
            </button>

            <button
              type="button"
              onClick={() => {
                loadHistorique();
                setView(MODES.historique);
              }}
              className="group text-left p-6 bg-white rounded-2xl border-2 border-violet-100 hover:border-violet-400 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-4 group-hover:bg-violet-600 transition-colors">
                <History className="h-7 w-7 text-violet-700 group-hover:text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Historique</h3>
              <p className="text-sm text-slate-500 mt-1">Ventes de la session en cours</p>
            </button>

            <button
              type="button"
              onClick={async () => {
                setFermetureWarning(null);
                const data = await loadSession();
                const r = data?.recap;
                const fond = round2(r?.fondDeCaisse ?? 0);
                const attendu = round2(
                  r?.especesEnCaisse ?? r?.totalEspeces ?? fond
                );
                setEspecesComptees(attendu > 0 ? String(attendu) : '');
                setView(MODES.cloture);
              }}
              className="sm:col-span-2 group text-left p-6 bg-white rounded-2xl border-2 border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center group-hover:bg-slate-700 transition-colors shrink-0">
                <Lock className="h-6 w-6 text-slate-600 group-hover:text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Clôturer la caisse</h3>
                <p className="text-sm text-slate-500">Fin de journée et rapport</p>
              </div>
            </button>
          </div>
        </div>
      )}

      {showTicketDetail && ticketDetail && (() => {
        const isOpen = ticketDetail.statut === 'EN_ATTENTE' && ticketDetail.statutPaiement === 'EN_ATTENTE';
        const isPaid = ticketDetail.statutPaiement === 'VALIDE' && ticketDetail.statut !== 'ANNULEE';
        const isCancelled = ticketDetail.statut === 'ANNULEE';
        const statutLabel = isCancelled ? 'Annulé' : isOpen ? 'En attente' : 'Terminé';
        const statutColor = isCancelled ? 'text-red-600' : isOpen ? 'text-orange-600' : 'text-emerald-600';
        const paiementLabel = ticketDetail.statutPaiement === 'VALIDE' ? 'Payé'
          : ticketDetail.statutPaiement === 'REMBOURSE' ? 'Remboursé'
          : ticketDetail.statutPaiement === 'EN_ATTENTE' ? 'Non payé'
          : ticketDetail.statutPaiement;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { setShowTicketDetail(false); setTicketFideliteDisplay(null); }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {ticketDetail.numeroTicket || ticketDetail.numero || ticketDetail.id.slice(-8)}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {new Date(ticketDetail.dateCommande).toLocaleString('fr-FR')}
                  </p>
                </div>
                <button type="button" onClick={() => { setShowTicketDetail(false); setTicketFideliteDisplay(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {isCancelled && (
                <div className="bg-red-50 border-b border-red-200 px-6 py-2 text-center">
                  <span className="text-sm font-bold text-red-700">TICKET ANNULÉ</span>
                </div>
              )}

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Statut</p>
                    <p className={`font-semibold ${statutColor}`}>{statutLabel}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-500">Paiement</p>
                    <p className="font-semibold text-slate-900">{paiementLabel}</p>
                  </div>
                  {ticketDetail.methodePaiement && ticketDetail.statutPaiement !== 'EN_ATTENTE' && (
                    <div className="bg-slate-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-slate-500">Méthode</p>
                      <p className="font-semibold text-slate-900">{ticketDetail.methodePaiement}</p>
                    </div>
                  )}
                  {ticketDetail.user && (
                    <div className="bg-slate-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-slate-500">Client</p>
                      <p className="font-semibold text-slate-900 truncate">{ticketDetail.user.prenom} {ticketDetail.user.nom}</p>
                    </div>
                  )}
                </div>

                {ticketFideliteDisplay?.phase && (
                  <div
                    className={`rounded-xl px-4 py-3 border ${
                      ticketFideliteDisplay.phase === 'intent'
                        ? 'bg-violet-50 border-violet-200'
                        : 'bg-emerald-50 border-emerald-200'
                    }`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1 text-slate-600">
                      {ticketFideliteDisplay.phase === 'intent'
                        ? 'Intention fidélité'
                        : 'Récompense appliquée'}
                    </p>
                    <ul className="space-y-0.5">
                      {(ticketFideliteDisplay.lines || []).map((line) => (
                        <li
                          key={line}
                          className={`text-sm ${
                            ticketFideliteDisplay.phase === 'intent'
                              ? 'text-violet-900'
                              : 'text-emerald-900'
                          }`}
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                    {ticketFideliteDisplay.phase === 'intent' && (
                      <p className="text-xs text-violet-700 mt-2">
                        Pour remplacer ou retirer : reprendre le ticket, puis choisir une autre récompense ou « Conserver les points ».
                      </p>
                    )}
                  </div>
                )}

                {(() => {
                  const encaissements = (ticketDetail.paiements || []).filter((p) => p.montant >= 0);
                  const remboursements = (ticketDetail.paiements || []).filter((p) => p.montant < 0);
                  const totalRembourse = remboursements.reduce((s, p) => s + Math.abs(p.montant), 0);
                  const totalNet = ticketDetail.total - totalRembourse;
                  const refundedLineIds = new Set();
                  remboursements.forEach((p) => {
                    try { const meta = typeof p.reference === 'string' ? JSON.parse(p.reference) : p.reference; (meta?.ligneIds || []).forEach((id) => refundedLineIds.add(id)); } catch {}
                  });
                  const hasRefunds = remboursements.length > 0;
                  const soldeRemboursable = ticketDetail.total - totalRembourse;

                  return (
                    <>
                      <div className="border-t border-slate-100 pt-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Articles ({(ticketDetail.produits || []).length})</h4>
                        <div className="space-y-2">
                          {(ticketDetail.produits || []).map((lp) => {
                            const isRefunded = refundedLineIds.has(lp.id);
                            return (
                              <div key={lp.id} className={`flex justify-between items-start gap-2 rounded-lg px-3 py-2 ${isRefunded ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${isRefunded ? 'text-orange-400 line-through' : 'text-slate-900'}`}>{lp.produit?.nom || 'Produit'}</p>
                                  {isRefunded && <span className="text-xs font-semibold text-orange-600">Remboursé</span>}
                                  {lp.commentaire && <p className="text-xs text-slate-400 whitespace-pre-line mt-0.5">{lp.commentaire}</p>}
                                  {lp.poidsSouhaite && <p className="text-xs text-slate-400">{lp.poidsSouhaite}g{lp.typeDecoupe ? ` · ${lp.typeDecoupe}` : ''}</p>}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-sm text-slate-500">x{lp.quantite}</p>
                                  <p className={`text-sm font-bold ${isRefunded ? 'text-orange-400 line-through' : ''}`}>{fmt(lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {encaissements.length > 0 && (
                        <div className="border-t border-slate-100 pt-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-3">Encaissements</h4>
                          <div className="space-y-1.5">
                            {encaissements.map((p) => (
                              <div key={p.id} className="flex justify-between text-sm bg-emerald-50 rounded-lg px-3 py-2">
                                <span className="text-slate-700 font-medium">{p.methodePaiement}</span>
                                <span className="font-bold text-emerald-700">{fmt(p.montant)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {hasRefunds && (
                        <div className="border-t border-orange-200 pt-4">
                          <h4 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-1.5">
                            <RotateCcw className="h-4 w-4" />
                            Remboursements ({remboursements.length})
                          </h4>
                          <div className="space-y-1.5">
                            {remboursements.map((p) => {
                              let savMeta = null;
                              try { savMeta = typeof p.reference === 'string' ? JSON.parse(p.reference) : p.reference; } catch {}
                              const typeLabel = savMeta?.type === 'REMBOURSEMENT_COMPLET' ? 'Complet'
                                : savMeta?.type === 'REMBOURSEMENT_ARTICLES' ? 'Par articles'
                                : savMeta?.type === 'REMBOURSEMENT_LIBRE' ? 'Montant libre'
                                : 'Remboursement';
                              return (
                                <div key={p.id} className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-orange-800 font-medium">{typeLabel} · {p.methodePaiement}</span>
                                    <span className="font-bold text-orange-700">-{fmt(Math.abs(p.montant))}</span>
                                  </div>
                                  {savMeta?.motif && (
                                    <p className="text-xs text-orange-600 mt-1">Motif : {savMeta.motif}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-slate-200 pt-4 space-y-1">
                        {ticketFideliteDisplay?.phase === 'applied' &&
                          ticketFideliteDisplay.reductionEur > 0 && (
                          <div className="flex justify-between items-baseline text-sm">
                            <span className="text-emerald-700">
                              Fidélité{ticketFideliteDisplay.libelle ? ` · ${ticketFideliteDisplay.libelle}` : ''}
                              {ticketFideliteDisplay.points != null
                                ? ` (${ticketFideliteDisplay.points} pts)`
                                : ''}
                            </span>
                            <span className="font-semibold text-emerald-700">
                              −{fmt(ticketFideliteDisplay.reductionEur)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-baseline">
                          <span className="text-lg font-bold text-slate-900">Total</span>
                          <span className={`text-2xl font-bold ${isCancelled ? 'text-red-500 line-through' : hasRefunds ? 'text-slate-400 line-through text-lg' : 'text-emerald-600'}`}>{fmt(ticketDetail.total)}</span>
                        </div>
                        {hasRefunds && (
                          <>
                            <div className="flex justify-between items-baseline">
                              <span className="text-sm text-orange-600">Remboursé</span>
                              <span className="text-sm font-bold text-orange-600">-{fmt(totalRembourse)}</span>
                            </div>
                            <div className="flex justify-between items-baseline">
                              <span className="text-lg font-bold text-slate-900">Total net</span>
                              <span className={`text-2xl font-bold ${totalNet <= 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmt(totalNet)}</span>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border-t border-slate-100 pt-4 space-y-2">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Actions</h4>

                        {isOpen && (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => { setShowTicketDetail(false); reprendreTicket(ticketDetail); }}
                              className="flex-1 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 flex items-center justify-center gap-2">
                              <PlayCircle className="h-5 w-5" />
                              Reprendre et encaisser
                            </button>
                            <button type="button" onClick={() => { annulerTicketOuvert(ticketDetail.id); setShowTicketDetail(false); }}
                              className="py-3 px-4 bg-red-100 text-red-700 font-semibold rounded-xl hover:bg-red-200 flex items-center gap-1">
                              <XCircle className="h-4 w-4" /> Annuler
                            </button>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <button type="button" onClick={() => imprimerDuplicata(ticketDetail, ticketFideliteDisplay)}
                            className="flex-1 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center justify-center gap-2">
                            <Printer className="h-4 w-4" /> Imprimer duplicata
                          </button>
                          <button type="button" onClick={() => {
                            const fidLines = (ticketFideliteDisplay?.lines || []).join('\n');
                            const text = `${ticketDetail.numeroTicket || ticketDetail.numero}\n${new Date(ticketDetail.dateCommande).toLocaleString('fr-FR')}\n${(ticketDetail.produits || []).map(lp => `${lp.produit?.nom} x${lp.quantite} ${fmt(lp.prixTotal || lp.prixUnitaire * lp.quantite)}`).join('\n')}${fidLines ? `\n${fidLines}` : ''}\nTotal: ${fmt(ticketDetail.total)}`;
                            navigator.clipboard?.writeText(text); showToast('Copié dans le presse-papier', 'info');
                          }}
                            className="py-2.5 px-4 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-1">
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>

                        {isPaid && soldeRemboursable > 0.01 && (
                          <button type="button" onClick={ouvrirSavModal}
                            className="w-full py-3 bg-orange-50 text-orange-700 font-semibold rounded-xl hover:bg-orange-100 border border-orange-200 flex items-center justify-center gap-2">
                            <RotateCcw className="h-4 w-4" /> Rembourser
                          </button>
                        )}

                        {isPaid && (
                          <button type="button" onClick={() => annulerTicketPaye(ticketDetail.id)}
                            className="w-full py-2.5 bg-red-50 text-red-700 font-semibold rounded-xl hover:bg-red-100 border border-red-200 flex items-center justify-center gap-2">
                            <XCircle className="h-4 w-4" /> Annuler ce ticket (avec motif)
                          </button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {showSavModal && ticketDetail && (() => {
        const remboursementsDeja = (ticketDetail.paiements || []).filter((p) => p.montant < 0);
        const totalRembourse = remboursementsDeja.reduce((s, p) => s + Math.abs(p.montant), 0);
        const soldeRemboursable = ticketDetail.total - totalRembourse;
        const refundedIds = new Set();
        remboursementsDeja.forEach((p) => {
          try { const meta = typeof p.reference === 'string' ? JSON.parse(p.reference) : p.reference; (meta?.ligneIds || []).forEach((id) => refundedIds.add(id)); } catch {}
        });
        const lignesDisponibles = (ticketDetail.produits || []).filter((lp) => !refundedIds.has(lp.id));
        const selectedTotal = lignesDisponibles.reduce((s, lp) => {
          const qty = savLineQty[lp.id] || 0;
          if (qty <= 0) return s;
          const unitPrice = (lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite) / lp.quantite;
          return s + unitPrice * qty;
        }, 0);
        const hasSelectedLines = Object.values(savLineQty).some((q) => q > 0);
        const montantPreview = savTab === 'complet' ? soldeRemboursable
          : savTab === 'articles' ? selectedTotal
          : (parseFloat(savMontantLibre) || 0);
        const isValid = savTab === 'complet' ? true
          : savTab === 'articles' ? hasSelectedLines
          : parseFloat(savMontantLibre) > 0;

        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSavModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-orange-200 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-orange-600" />
                  <h3 className="text-lg font-bold text-slate-900">Remboursement</h3>
                </div>
                <button type="button" onClick={() => setShowSavModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              <div className="bg-orange-50 border-b border-orange-200 px-6 py-2 flex justify-between text-sm">
                <span className="text-orange-700">Solde remboursable</span>
                <span className="font-bold text-orange-800">{fmt(soldeRemboursable)}</span>
              </div>

              <div className="px-6 pt-4">
                <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
                  {[
                    { key: 'articles', label: 'Par article' },
                    { key: 'libre', label: 'Montant libre' },
                    { key: 'complet', label: 'Complet' },
                  ].map((tab) => (
                    <button key={tab.key} type="button"
                      onClick={() => setSavTab(tab.key)}
                      className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${savTab === tab.key ? 'bg-white text-orange-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6 space-y-4">
                {savTab === 'articles' && (
                  <div className="space-y-2">
                    {lignesDisponibles.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Tous les articles ont déjà été remboursés</p>
                    ) : lignesDisponibles.map((lp) => {
                      const qtySelected = savLineQty[lp.id] || 0;
                      const isActive = qtySelected > 0;
                      const unitPrice = (lp.prixTotal || lp.prixFinal || lp.prixUnitaire * lp.quantite) / lp.quantite;
                      return (
                        <div key={lp.id} className={`rounded-xl px-4 py-3 border-2 transition-colors ${isActive ? 'border-orange-400 bg-orange-50' : 'border-slate-200'}`}>
                          <div className="flex items-center gap-3">
                            <button type="button"
                              onClick={() => setSavLineQty((prev) => ({ ...prev, [lp.id]: isActive ? 0 : lp.quantite }))}
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${isActive ? 'bg-orange-500 border-orange-500' : 'border-slate-300 hover:border-slate-400'}`}>
                              {isActive && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{lp.produit?.nom || 'Produit'}</p>
                              <p className="text-xs text-slate-400">{fmt(unitPrice)} / unité</p>
                            </div>
                            <span className="text-sm font-bold text-slate-700">{fmt(unitPrice * qtySelected)}</span>
                          </div>
                          {isActive && lp.quantite > 1 && (
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-orange-200">
                              <span className="text-xs text-orange-700">Quantité à rembourser</span>
                              <div className="flex items-center gap-2">
                                <button type="button"
                                  onClick={() => setSavLineQty((prev) => ({ ...prev, [lp.id]: Math.max(1, qtySelected - 1) }))}
                                  className="w-7 h-7 rounded-lg bg-orange-200 text-orange-800 font-bold flex items-center justify-center hover:bg-orange-300">
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="text-sm font-bold text-orange-800 w-8 text-center">{qtySelected} / {lp.quantite}</span>
                                <button type="button"
                                  onClick={() => setSavLineQty((prev) => ({ ...prev, [lp.id]: Math.min(lp.quantite, qtySelected + 1) }))}
                                  className="w-7 h-7 rounded-lg bg-orange-200 text-orange-800 font-bold flex items-center justify-center hover:bg-orange-300">
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {savTab === 'libre' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Montant à rembourser</label>
                    <div className="relative">
                      <input type="number" step="0.01" min="0.01" max={soldeRemboursable}
                        value={savMontantLibre} onChange={(e) => setSavMontantLibre(e.target.value)}
                        placeholder="0.00" className="w-full text-2xl font-bold text-center border-2 border-slate-200 rounded-xl px-4 py-4 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
                        autoFocus />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-slate-400">€</span>
                    </div>
                    {parseFloat(savMontantLibre) > soldeRemboursable && (
                      <p className="text-xs text-red-500 mt-1">Dépasse le solde remboursable ({fmt(soldeRemboursable)})</p>
                    )}
                  </div>
                )}

                {savTab === 'complet' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-orange-700">Remboursement intégral du solde restant</p>
                    <p className="text-3xl font-bold text-orange-800 mt-2">{fmt(soldeRemboursable)}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Motif (optionnel)</label>
                  <input type="text" value={savMotif} onChange={(e) => setSavMotif(e.target.value)}
                    placeholder="Ex: Article défectueux, geste commercial..."
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mode de remboursement</label>
                  <div className="flex gap-2">
                    {['ESPECES', 'CARTE'].map((m) => (
                      <button key={m} type="button" onClick={() => setSavMethode(m)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${savMethode === m ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                        {m === 'ESPECES' ? 'Espèces' : 'Carte'}
                      </button>
                    ))}
                  </div>
                </div>

                {montantPreview > 0 && (
                  <div className="bg-slate-50 rounded-xl px-4 py-3 flex justify-between items-baseline">
                    <span className="text-sm text-slate-600">Montant remboursé</span>
                    <span className="text-xl font-bold text-orange-700">{fmt(montantPreview)}</span>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex gap-3">
                <button type="button" onClick={() => setShowSavModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200">
                  Annuler
                </button>
                <button type="button" onClick={handleRemboursement}
                  disabled={!isValid || savLoading || montantPreview <= 0 || montantPreview > soldeRemboursable + 0.01}
                  className="flex-1 py-3 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  {savLoading ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
